import React, { useMemo } from 'react';
import { Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Tooltip } from './Tooltip';
import usePlatform from '@/utils/hooks/usePlatform';


/**
 * RelativeTime — renders a compact relative time string (5m, 7h, 3d, 23w, 1y)
 * with a Tooltip on web showing the full timestamp.
 *
 * Props:
 *   time: string | Date — the timestamp
 *   style: optional text style override
 */
const RelativeTime = ({ time, style }) => {
    const { isWeb } = usePlatform();

    const { relative, full } = useMemo(() => {
        const date = new Date(time);
        if(isNaN(date.getTime())) return { relative: '', full: '' };

        const now = new Date();
        const diffMs = now - date;
        const seconds = Math.floor(diffMs / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);
        const weeks = Math.floor(days / 7);
        const years = Math.floor(days / 365);

        let rel;
        if(seconds < 60) rel = 'just now';
        else if(minutes < 60) rel = `${minutes}m ago`;
        else if(hours < 24) rel = `${hours}h ago`;
        else if(days < 7) rel = `${days}d ago`;
        else if(weeks < 52) rel = `${weeks}w ago`;
        else rel = `${years}y ago`;

        const fullStr = date.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        });

        return { relative: rel, full: fullStr };
    }, [time]);

    if(!relative) return null;

    if(isWeb && full){
        return (
            <Tooltip title={full}>
                <Text style={[styles.text, style]}>{relative}</Text>
            </Tooltip>
        );
    }

    return <Text style={[styles.text, style]}>{relative}</Text>;
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    text: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: 12,
    },
}));

export default RelativeTime;
